"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import {
  formatDeliveryLocation,
  readStoredDeliveryLocation,
  storeDeliveryLocation,
  type DeliveryLocation,
} from "@/lib/checkout/delivery-location";
import { useSaveDeliveryAddress } from "./use-save-delivery-address";
import { useDeliveryAddressInput } from "./use-delivery-address-input";
import {
  savedAddressOptionsFrom,
  type SavedAddressOption,
} from "@/lib/checkout/saved-address-options";

export function useDeliveryLocation() {
  const { isAuthenticated, savedAddresses, ownerId, ownerReady } =
    useDeliveryOwner();
  const addressBook = useSaveDeliveryAddress(isAuthenticated && !!ownerId);
  const draft = useStoredDeliveryDraft(ownerReady, ownerId);
  const { chosenLocation, addressInput, inputDirty, lookingUp, setLookingUp } =
    draft;

  const savedAddressOptions = useMemo(
    () => savedAddressOptionsFrom(savedAddresses),
    [savedAddresses],
  );
  const { location, addressDisplay } = useResolvedDeliveryLocation(
    savedAddressOptions,
    chosenLocation,
    inputDirty,
    addressInput,
  );
  const { selectSavedAddress, onAddressChange } = useDeliveryAddressInput(
    addressBook,
    savedAddressOptions,
    draft,
  );

  const onSelectionResolving = useCallback(
    (resolving: boolean) => {
      setLookingUp(resolving);
    },
    [setLookingUp],
  );

  return {
    location,
    addressDisplay,
    onAddressChange,
    onSelectionResolving,
    lookingUp,
    addressPersistPending: !!chosenLocation && !ownerReady,
    hasUnresolvedInput:
      inputDirty && !!addressInput.trim() && !location && !lookingUp,
    addressBookNotice: addressBook.notice,
    isLoading:
      !draft.restored ||
      !ownerReady ||
      (isAuthenticated && savedAddresses === undefined),
    savedAddressOptions,
    selectSavedAddress,
  };
}

function useDeliveryOwner() {
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const savedAddresses = useQuery(
    api.addresses.queries.listMine,
    isAuthenticated ? { addressType: "shipping" } : "skip",
  );
  const currentUser = useQuery(
    api.users.queries.current,
    isAuthenticated ? {} : "skip",
  );
  const ownerId = isAuthenticated ? (currentUser?.user?._id ?? null) : null;
  const ownerReady = !authLoading && (!isAuthenticated || !!currentUser?.user);
  return { isAuthenticated, savedAddresses, ownerId, ownerReady };
}

function useStoredDeliveryDraft(ownerReady: boolean, ownerId: string | null) {
  const [chosenLocation, setChosenLocation] = useState<DeliveryLocation | null>(
    null,
  );
  const [addressInput, setAddressInput] = useState("");
  const [inputDirty, setInputDirty] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  const [restored, setRestored] = useState(false);
  if (ownerReady && !restored) {
    setRestored(true);
    if (!inputDirty) {
      const stored = readStoredDeliveryLocation(ownerId);
      if (stored) {
        setChosenLocation(stored);
        setAddressInput(formatDeliveryLocation(stored));
      }
    }
  }

  useEffect(() => {
    if (!ownerReady || !chosenLocation) return;
    storeDeliveryLocation(
      toPersistedLocation(chosenLocation, ownerId),
      ownerId,
    );
  }, [chosenLocation, ownerId, ownerReady]);

  return {
    chosenLocation,
    setChosenLocation,
    addressInput,
    setAddressInput,
    inputDirty,
    setInputDirty,
    lookingUp,
    setLookingUp,
    restored,
  };
}

function useResolvedDeliveryLocation(
  savedAddressOptions: SavedAddressOption[],
  chosenLocation: DeliveryLocation | null,
  inputDirty: boolean,
  addressInput: string,
) {
  const savedDefault = useMemo(() => {
    if (savedAddressOptions.length === 0) return null;
    return (
      savedAddressOptions.find((address) => address.isDefault) ?? savedAddressOptions[0]
    ).location;
  }, [savedAddressOptions]);

  const effectiveChosen = useMemo(() => {
    if (!chosenLocation) return null;
    if (!chosenLocation.savedAddressId) return chosenLocation;
    const option = savedAddressOptions.find(
      (option) => option.id === chosenLocation.savedAddressId,
    );
    return option ? option.location : chosenLocation;
  }, [chosenLocation, savedAddressOptions]);

  const location = inputDirty
    ? effectiveChosen
    : (effectiveChosen ?? savedDefault);
  const addressDisplay =
    inputDirty || chosenLocation
      ? addressInput
      : savedDefault
        ? formatDeliveryLocation(savedDefault)
        : addressInput;
  return { location, addressDisplay };
}

function toPersistedLocation(
  chosen: DeliveryLocation,
  ownerId: string | null,
): DeliveryLocation {
  const locality = { zip: chosen.zip, city: chosen.city, state: chosen.state };
  if (chosen.savedAddressId) {
    return { ...locality, savedAddressId: chosen.savedAddressId };
  }
  return ownerId ? chosen : locality;
}
